import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';

const CatalogPage = () => {
  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || 'all';
  const vendorParam = params.get('vendor') || 'all';
  const q = params.get('q') || '';
  const [sort, setSort] = useState('relevance');
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = 20;

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data));
    api.get('/vendors').then((r) => setVendors(r.data));
  }, []);

  useEffect(() => { setPage(1); }, [cat, vendorParam, q, sort]);

  useEffect(() => {
    setLoading(true);
    const query = new URLSearchParams();
    if (cat && cat !== 'all') query.set('category', cat);
    if (vendorParam && vendorParam !== 'all') query.set('vendor', vendorParam);
    if (q) query.set('q', q);
    if (sort) query.set('sort', sort);
    query.set('page', String(page));
    query.set('limit', String(limit));
    api.get(`/products?${query.toString()}`)
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [cat, vendorParam, q, sort, page]);

  const setFilter = (key, value) => {
    const p = new URLSearchParams(params);
    if (!value || value === 'all') p.delete(key);
    else p.set(key, value);
    setParams(p);
  };

  const clearAll = () => setParams({});

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 mt-6 pb-32">
      <h1 className="text-3xl md:text-4xl font-black text-white">Catálogo</h1>
      <p className="text-white/50 mt-1">Todo el vape shop de NubesMTY en un solo lugar.</p>

      <div className="mt-6 flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        <button onClick={() => setFilter('cat', 'all')} className={`px-4 h-10 rounded-full font-semibold shrink-0 border transition-colors ${cat === 'all' ? 'bg-orange-500 text-black border-orange-500' : 'bg-neutral-950 text-white/80 border-white/10 hover:border-orange-500/40'}`}>
          Todas
        </button>
        {categories.map((c) => (
          <button key={c.slug} onClick={() => setFilter('cat', c.name)} className={`px-4 h-10 rounded-full font-semibold shrink-0 border transition-colors ${cat === c.name ? 'bg-orange-500 text-black border-orange-500' : 'bg-neutral-950 text-white/80 border-white/10 hover:border-orange-500/40'}`}>
            {c.name} <span className={`ml-1 text-xs ${cat === c.name ? 'text-black/60' : 'text-white/40'}`}>{c.count}</span>
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm text-white/60 order-2 sm:order-1">
          {loading ? <><Loader2 className="inline w-4 h-4 animate-spin" /> Cargando...</> : (
            <>{total.toLocaleString('es-MX')} producto{total === 1 ? '' : 's'} {q && <>para "<span className="font-semibold text-white">{q}</span>"</>}</>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap order-1 sm:order-2">
          {(cat !== 'all' || vendorParam !== 'all' || q) && (
            <button onClick={clearAll} className="h-10 px-3 rounded-full bg-neutral-950 border border-white/10 text-white/70 hover:text-white text-sm flex items-center gap-1">
              <X className="w-4 h-4" /> Limpiar
            </button>
          )}
          <div className="flex items-center gap-2 text-white/70">
            <SlidersHorizontal className="w-4 h-4" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-full border border-white/10 bg-neutral-950 px-3 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/40">
              <option value="relevance">Relevancia</option>
              <option value="price-asc">Precio: menor a mayor</option>
              <option value="price-desc">Precio: mayor a menor</option>
              <option value="newest">Más nuevos</option>
            </select>
          </div>
          <select value={vendorParam} onChange={(e) => setFilter('vendor', e.target.value)} className="h-10 rounded-full border border-white/10 bg-neutral-950 px-3 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/40 max-w-[160px] truncate">
            <option value="all">Todas las marcas</option>
            {vendors.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </select>
        </div>
      </div>

      {items.length === 0 && !loading ? (
        <div className="mt-16 text-center py-16 border border-dashed border-white/10 rounded-2xl">
          <Search className="w-8 h-8 mx-auto text-white/40" />
          <p className="mt-3 font-semibold text-white/80">Sin resultados</p>
          <p className="text-sm text-white/50">Prueba con otra búsqueda o categoría.</p>
        </div>
      ) : (cat === 'all' && !q ? (
        (() => {
          // Group current page's items by category, in the order defined by /categories.
          const map = new Map();
          items.forEach((p) => {
            const key = p.category || 'Sin categoría';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
          });
          const orderMap = new Map(categories.map((c, i) => [c.name, c.order ?? i]));
          const groups = Array.from(map.entries())
            .sort((a, b) => (orderMap.get(a[0]) ?? 999) - (orderMap.get(b[0]) ?? 999) || a[0].localeCompare(b[0]));
          return (
            <div className="mt-6 space-y-10" data-testid="catalog-grouped">
              {groups.map(([name, group]) => (
                <div key={name}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl md:text-2xl font-black text-white flex items-center gap-2">
                      <span className="inline-block w-2 h-6 rounded-sm bg-orange-500"></span>
                      {name}
                      <span className="text-sm font-semibold text-white/40 ml-1">({group.length})</span>
                    </h3>
                    <button onClick={() => setFilter('cat', name)} className="text-orange-400 text-xs font-bold hover:text-orange-300 inline-flex items-center gap-1 uppercase tracking-wider">
                      Ver {name} <span aria-hidden>→</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                    {group.map((p) => <ProductCard key={p.product_id} product={p} />)}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      ))}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(page - 1)} className="h-10 px-4 rounded-full bg-neutral-950 border border-white/10 text-white disabled:opacity-40">Anterior</button>
          <span className="text-white/70 text-sm">Página {page} de {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="h-10 px-4 rounded-full bg-orange-500 text-black font-semibold disabled:opacity-40">Siguiente</button>
        </div>
      )}
    </div>
  );
};

export default CatalogPage;
